/**
 * V2 sync (Theme 10, from 7cc48382 — SPA telemetry shim).
 *
 * Telemetry — client-side error forwarding to the gdb-server `/api/telemetry/event`
 * endpoint, which gates by `is_super_admin OR is_tester` and re-emits via the
 * Rust OTLP logs exporter so events land in SigNoz.
 *
 * This module is a thin transport wrapper. The decision of *whether* to send
 * lives at the call site (typically: `if (authStatus.user?.telemetryEnabled)`).
 * Server-side gating is the safety net — if a non-tester ever reaches this
 * path, the server returns 200 + drops silently.
 *
 * Surface independence: the function is provider-free (just `fetch`) so it can
 * be called from anywhere — global error handlers, React error boundaries,
 * mutation onError callbacks, manual breadcrumb pushes.
 */

import { getApiBaseUrl } from '@/lib/api-client'

export type TelemetryLevel = 'error' | 'warn' | 'info'

export interface TelemetryEvent {
  level: TelemetryLevel
  message: string
  /** Free-form source tag — `'spa'` here, `'plugin'` from UE. Filterable in SigNoz. */
  source: string
  /** Caller-supplied context bag. Browser UA, route, error stack, etc. */
  context?: Record<string, unknown>
}

/**
 * POST a telemetry event to the gdb-server. Fire-and-forget — never throws,
 * never awaits, never blocks the calling code path. The whole point of
 * telemetry is to be invisible when working and silent when failing.
 *
 * `getToken` returns the user's session JWT (or null when not logged in).
 * The dependency is injected so this module doesn't have to know about the
 * `useAuth` hook lifecycle — it can be called from non-React contexts
 * (window.onerror handlers run before any React tree exists).
 */
export function sendTelemetryEvent(
  event: TelemetryEvent,
  getToken: () => string | null,
): void {
  const token = getToken()
  if (!token) {
    // Anonymous → server would drop anyway. Skip the network round-trip.
    return
  }

  // fetch() promise is intentionally unused — this is a fire-and-forget POST.
  // We attach `.catch(() => {})` to silence the unhandledrejection that an
  // ignored rejected promise would otherwise produce.
  fetch(`${getApiBaseUrl()}/api/telemetry/event`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(event),
    // keepalive lets the request survive a pagehide / navigation, important
    // for catching errors that happen during unload.
    keepalive: true,
  }).catch(() => {
    // Intentionally silent — see top of file.
  })
}
